import { useMemo } from 'react'
import { Box, Flex, Stack } from '@storyofams/react-ui'
import { withStory, WithStoryProps, Image } from '@storyofams/storyblok-toolkit'
import { intersection } from 'lodash'
import { GetStaticProps, GetStaticPaths, NextPage } from 'next'
import { useRouter } from 'next/router'
import { defineMessages, FormattedMessage } from 'react-intl'

import { useQuery } from 'react-query'
import {
  Seo,
  RichText,
  Heading,
  FixedRatio,
  Container,
  Header,
  Button,
} from '~components'
import { enhancedStaticProps, withLayout } from '~lib'
import { ArticlesGrid, ArticleInfo } from '~components/blog'
import { storyblokSdk } from '~lib/storyblok/client'

const messages = defineMessages({
  relatedArticles: 'Related articles',
  goToShop: 'Go to shop',
})

interface BlogPageProps extends WithStoryProps {
  story: any
  pageHandle: string
}

const BlogPage: NextPage<BlogPageProps> = ({ story, pageHandle }) => {
  const { locale, defaultLocale } = useRouter()
  const fetchBlogPosts = async () => {
    const data = (
      await storyblokSdk.blogPages({
        prefix: `${defaultLocale !== locale ? `${locale}/*` : ''}`,
      })
    ).BlogpageItems

    return data.items
  }

  const { data: blogPosts } = useQuery(['blogPosts'], fetchBlogPosts)

  const postCategoriesIds = story?.content?.categories?.map(
    ({ content }) => content?._uid,
  )

  const relatedArticles = useMemo(
    () =>
      blogPosts
        ?.filter(
          post =>
            post?.slug !== story?.slug &&
            !!intersection(
              postCategoriesIds,
              post.content?.categories?.map(({ content }) => content._uid),
            ).length,
        )
        .slice(0, 2),
    [blogPosts],
  )

  const renderContent = (item, i) => {
    switch (item.component) {
      case 'blog_rich-text':
        return (
          <Box
            key={`blog_rich_text-${i}`}
            width="100%"
            pr={[0, 0, 180]}
            mt={[4, 8]}
            css={{ '&:first-child': { marginTop: 0 } }}
          >
            <Heading variant="h4" as="h4" mb={[2, 3]}>
              {item.title}
            </Heading>

            <RichText text={item.content} />
          </Box>
        )

      case 'blog_image':
        return (
          <FixedRatio
            key={`blog_image-${i}`}
            ratio={[
              [327, 240],
              [996, 400],
            ]}
            position="relative"
            width={{ md: 'calc(100% + 204px)' }}
            mt={[4, 8]}
            ml={{ md: '-204px' }}
            css={{
              '.storyblok-image-wrapper': { width: '100%', height: '100%' },
              '&:first-child': { marginTop: 0 },
            }}
          >
            <Image
              alt={item.image?.alt || ''}
              src={item.image?.filename}
              fluid={1200}
              fit="cover"
            />
          </FixedRatio>
        )
      default:
        break
    }
  }

  return (
    <>
      <Seo story={story} pageHandle={pageHandle} />

      <Header
        title={story?.content?.text}
        img={story?.content?.image}
        backTo="/blog"
      />

      <Container space={8} bg="grey200">
        <Stack
          alignItems="flex-start"
          flexDirection={['column', 'column', 'row']}
          space={['36px', '36px', 3]}
        >
          <ArticleInfo
            person={story?.content?.person?.content}
            date={story?.created_at}
            image={story?.content?.image}
            description={story?.content?.text}
          />

          <Flex flexDirection="column">
            {story?.content?.content?.map(renderContent)}
          </Flex>
        </Stack>

        <Flex css={{ button: { backgroundColor: 'transparent' } }}>
          <Button
            variant="secondary"
            mx="auto"
            //to="/order/select-box?ordering=true"
            to="/meals?ordering=true"
          >
            <FormattedMessage {...messages.goToShop} />
          </Button>
        </Flex>

        <Flex flexDirection="column">
          <Heading
            variant="h3"
            as="h3"
            mb={[3, 5]}
            textAlign={{ sm: 'center' }}
          >
            <FormattedMessage {...messages.relatedArticles} />
          </Heading>

          <ArticlesGrid articles={relatedArticles} />
        </Flex>
      </Container>
    </>
  )
}

export default withLayout(withStory(BlogPage))

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const pages = (await storyblokSdk.blogPages()).BlogpageItems

  return {
    paths: pages?.items
      ?.map(({ slug }) => ({
        slug,
      }))
      ?.map(params => locales.map(locale => ({ params, locale })))
      ?.flat(),
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps<WithStoryProps> =
  enhancedStaticProps(async ({ sdk, params, defaultLocale, locale }) => {
    let story = null
    let notFound = false

    try {
      story = (
        await sdk.blogPage({
          id: `${defaultLocale !== locale ? `${locale}/` : ''}blog/${
            params.slug
          }`,
        })
      ).BlogpageItem
    } catch (e) {
      notFound = true
    }

    if (notFound || !story) {
      return {
        props: {
          story: {},
          pageHandle: params.slug,
        },
        notFound: true,
        revalidate:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
      }
    }

    return {
      props: {
        story,
        pageHandle: params.slug,
      },
      notFound: notFound || !story,
      revalidate:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
    }
  })
